/**
 * NotesTab Component
 * Displays and allows adding notes for a contact
 * Reads from GraphQL notes, writes via Remi REST API
 * Uses Tiptap rich text editor for note creation
 */

import React, { useState, useCallback } from 'react';
import { useEditor, EditorContent, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Button, Modal, useToast } from '@crisellevaldez/speculo-ui';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Undo,
  Redo,
  Send,
  StickyNote,
  Trash2,
  Pencil,
  Check,
  X,
  Plus,
} from 'lucide-react';
import { EmptyStateWithIcon } from '../../../common/EmptyStateWithIcon';
import { useContacts } from '../../../../hooks/useContacts';
import { useAuthStore } from '../../../../contexts/auth/useAuthStore';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { Contact } from '../../../../types/contact.types';
import { cn } from '../../../../utils/cn';
import { formatDate } from '../../../../utils/dateTime';
import { ConfirmationDialog } from '../../../common/dialogs/ConfirmationDialog';
import type { GQLNoteItem } from '../../../../hooks/graphql/contacts/useContact';

interface NotesTabProps {
  contact: Contact;
  onNoteAdded?: () => void;
  notes?: { items: GQLNoteItem[]; total: number; hasMore: boolean };
}

// Toolbar button component
const ToolbarButton: React.FC<{
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title: string;
}> = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={cn(
      'p-2 rounded transition-colors note-toolbar-btn',
      isActive && 'active',
      disabled && 'opacity-50 cursor-not-allowed'
    )}
  >
    {children}
  </button>
);

// Inline rich-text editor reused for note updates. Mounts a fresh Tiptap
// instance so the user gets the same Bold/Italic/List toolbar they have for
// new notes, instead of a plain textarea that would strip HTML formatting.
const NoteEditFormTiptap: React.FC<{
  initialContent: string;
  onSave: (html: string) => Promise<void>;
  onCancel: () => void;
}> = ({ initialContent, onSave, onCancel }) => {
  const [isSaving, setIsSaving] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Placeholder.configure({ placeholder: 'Edit your note...' }),
    ],
    content: initialContent,
    editorProps: {
      attributes: {
        class:
          'text-sm max-w-none focus:outline-none min-h-[80px] px-3 py-2 ' +
          '[&_p]:my-1 ' +
          '[&_h2]:font-semibold [&_h2]:text-base [&_h2]:my-2 ' +
          '[&_h3]:font-semibold [&_h3]:text-sm [&_h3]:my-2 ' +
          '[&_ul]:list-disc [&_ul]:pl-5 ' +
          '[&_ol]:list-decimal [&_ol]:pl-5 ' +
          '[&_li]:my-0.5',
      },
    },
  });

  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBold: editor?.isActive('bold') ?? false,
      isItalic: editor?.isActive('italic') ?? false,
      isBulletList: editor?.isActive('bulletList') ?? false,
      isOrderedList: editor?.isActive('orderedList') ?? false,
      canUndo: editor?.can().undo() ?? false,
      canRedo: editor?.can().redo() ?? false,
    }),
  });

  const handleSave = async () => {
    if (!editor) return;
    const html = editor.getHTML();
    if (html === initialContent) {
      onCancel();
      return;
    }
    setIsSaving(true);
    try {
      await onSave(html);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="border rounded-md note-editor-wrap overflow-hidden">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b note-editor-toolbar">
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleBold().run()}
          isActive={editorState?.isBold}
          disabled={!editor}
          title="Bold"
        >
          <Bold className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          isActive={editorState?.isItalic}
          disabled={!editor}
          title="Italic"
        >
          <Italic className="w-3.5 h-3.5" />
        </ToolbarButton>
        <div className="w-px h-5 note-toolbar-divider mx-1" />
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          isActive={editorState?.isBulletList}
          disabled={!editor}
          title="Bullet List"
        >
          <List className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          isActive={editorState?.isOrderedList}
          disabled={!editor}
          title="Numbered List"
        >
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarButton>
        <div className="w-px h-5 note-toolbar-divider mx-1" />
        <ToolbarButton
          onClick={() => editor?.chain().focus().undo().run()}
          disabled={!editorState?.canUndo}
          title="Undo"
        >
          <Undo className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().redo().run()}
          disabled={!editorState?.canRedo}
          title="Redo"
        >
          <Redo className="w-3.5 h-3.5" />
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} className="note-editor-body" />
      <div className="flex justify-end gap-1 px-2 py-1.5 border-t note-editor-footer">
        <button
          onClick={onCancel}
          disabled={isSaving}
          className="p-1.5 rounded-md note-card-delete disabled:opacity-50"
          title="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving || !editor}
          className="p-1.5 rounded-md note-card-delete disabled:opacity-50"
          title="Save"
        >
          <Check className="w-4 h-4 text-green-600" />
        </button>
      </div>
    </div>
  );
};

// Single note card component - styled like a sticky note
const NoteCard: React.FC<{
  note: GQLNoteItem;
  timezone?: string;
  onDelete: () => void;
  onSave: (body: string) => Promise<void>;
  isDeleting?: boolean;
}> = ({ note, timezone, onDelete, onSave, isDeleting }) => {
  const [isEditing, setIsEditing] = useState(false);

  const startEdit = () => setIsEditing(true);
  const cancelEdit = () => setIsEditing(false);
  const handleSaveEdit = async (html: string) => {
    await onSave(html);
    setIsEditing(false);
  };

  return (
    <div className="relative note-card rounded-md overflow-hidden group">
      {!isEditing && (
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all">
          <button
            onClick={startEdit}
            className="p-1.5 rounded-md note-card-delete"
            title="Edit note"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            disabled={isDeleting}
            className="p-1.5 rounded-md note-card-delete disabled:opacity-50"
            title="Delete note"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="p-3">
        {/* Header with author and time */}
        <div className="flex items-center gap-2 mb-2">
          <span className="font-semibold note-card-author text-sm">
            {note.authorName || 'Unknown'}
          </span>
          <span className="note-card-dot">&bull;</span>
          <span className="note-card-time text-xs">
            {formatDate(note.createdAt, timezone, 'MMM D, YYYY h:mm A z')}
          </span>
        </div>

        {isEditing ? (
          <NoteEditFormTiptap
            initialContent={note.body}
            onSave={handleSaveEdit}
            onCancel={cancelEdit}
          />
        ) : (
          <div
            className="text-[13px] note-card-body [&_p]:my-1 [&_h2]:font-semibold [&_h2]:text-sm [&_h3]:font-semibold [&_h3]:text-[13px] [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:my-0.5"
            dangerouslySetInnerHTML={{ __html: note.body }}
          />
        )}
      </div>
    </div>
  );
};

export const NotesTab: React.FC<NotesTabProps> = ({ contact, onNoteAdded, notes: notesProp }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<GQLNoteItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const { addHandoffNote, deleteHandoffNote, updateHandoffNote } = useContacts();
  const { user } = useAuthStore();
  const { organization } = useOrganizationStore();
  const { showToast } = useToast();

  const timezone = organization?.timezone || 'America/New_York';

  // Initialize Tiptap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Placeholder.configure({
        placeholder: 'Write your note here...',
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class:
          'text-sm max-w-none focus:outline-none min-h-[100px] px-4 py-3 ' +
          '[&_p]:my-1 ' +
          '[&_h2]:font-semibold [&_h2]:text-base [&_h2]:my-2 ' +
          '[&_h3]:font-semibold [&_h3]:text-sm [&_h3]:my-2 ' +
          '[&_ul]:list-disc [&_ul]:pl-5 ' +
          '[&_ol]:list-decimal [&_ol]:pl-5 ' +
          '[&_li]:my-0.5',
      },
    },
    onUpdate: ({ editor }) => {
      // Track content changes to enable/disable submit button
      const text = editor.getText().trim();
      setHasContent(text.length > 0);
    },
  });

  // Subscribe to editor state so toolbar active/disabled flags re-render on
  // selection or transaction changes. Without this, isActive() returns the
  // right value at call time but React never re-renders to reflect it.
  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBold: editor?.isActive('bold') ?? false,
      isItalic: editor?.isActive('italic') ?? false,
      isBulletList: editor?.isActive('bulletList') ?? false,
      isOrderedList: editor?.isActive('orderedList') ?? false,
      canUndo: editor?.can().undo() ?? false,
      canRedo: editor?.can().redo() ?? false,
    }),
  });

  // Get notes from GraphQL, sorted newest first
  const noteItems = [...(notesProp?.items ?? [])].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  // Handle note submission via Remi REST API
  const handleSubmit = useCallback(async () => {
    if (!editor || !hasContent || !user) {
      showToast({
        message: 'Please write a note before submitting',
        variant: 'error',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const noteContent = editor.getHTML();
      const userName = user.displayName || user.email || 'Unknown';

      await addHandoffNote(contact.id, {
        addedBy: user.uid,
        addedByName: userName,
        note: noteContent,
      });

      // Clear the editor and reset hasContent state
      editor.commands.clearContent();
      setHasContent(false);
      setAddOpen(false);

      showToast({
        message: 'Note added successfully',
        variant: 'success',
      });

      onNoteAdded?.();
    } catch (error) {
      console.error('Error adding note:', error);
      showToast({
        message: 'Failed to add note. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [editor, user, contact.id, addHandoffNote, showToast, onNoteAdded, hasContent]);

  const handleDeleteNote = useCallback(async () => {
    if (!noteToDelete) return;

    setIsDeleting(true);

    try {
      await deleteHandoffNote(contact.id, noteToDelete.noteId);

      showToast({
        message: 'Note deleted successfully',
        variant: 'success',
      });

      onNoteAdded?.();
    } catch (error) {
      console.error('Error deleting note:', error);
      showToast({
        message: 'Failed to delete note. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
      setNoteToDelete(null);
    }
  }, [noteToDelete, contact.id, deleteHandoffNote, showToast, onNoteAdded]);

  return (
    <div className="flex flex-col h-full min-h-0 gap-3">
      {/* Notes List */}
      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center gap-2 mb-3 flex-shrink-0">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
            Notes history
          </span>
          {noteItems.length > 0 && (
            <span className="text-[10.5px] tabular-nums text-muted-foreground">
              ({noteItems.length})
            </span>
          )}
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-foreground text-background hover:opacity-90 transition-opacity"
          >
            <Plus className="w-3 h-3" />
            Add note
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-custom">
          {noteItems.length === 0 ? (
            <EmptyStateWithIcon message="No notes yet" />
          ) : (
            <div className="space-y-3 pb-3">
              {noteItems.map((note, index) => (
                <NoteCard
                  key={note.noteId || `${note.createdAt}-${index}`}
                  note={note}
                  timezone={timezone}
                  onDelete={() => setNoteToDelete(note)}
                  onSave={async (body) => {
                    try {
                      await updateHandoffNote(contact.id, note.noteId, body);
                      showToast({ message: 'Note updated', variant: 'success' });
                      onNoteAdded?.();
                    } catch (error) {
                      console.error('Error updating note:', error);
                      showToast({ message: 'Failed to update note', variant: 'error' });
                      throw error;
                    }
                  }}
                  isDeleting={isDeleting}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Note modal — Tiptap editor stays mounted at the parent level
          so the buffer survives modal close-and-reopen cycles. */}
      <Modal isOpen={addOpen} onClose={() => setAddOpen(false)} size="sm">
        <Modal.Header>
          <h3 className="font-semibold text-base">Add note</h3>
        </Modal.Header>
        <Modal.Body>
          <div className="note-editor-wrap border rounded-lg overflow-hidden">
            <div className="flex flex-wrap items-center gap-1 px-2 sm:px-3 py-2 border-b note-editor-toolbar">
              <ToolbarButton
                onClick={() => editor?.chain().focus().toggleBold().run()}
                isActive={editorState?.isBold}
                disabled={!editor}
                title="Bold"
              >
                <Bold className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor?.chain().focus().toggleItalic().run()}
                isActive={editorState?.isItalic}
                disabled={!editor}
                title="Italic"
              >
                <Italic className="w-4 h-4" />
              </ToolbarButton>
              <div className="w-px h-6 note-toolbar-divider mx-1" />
              <ToolbarButton
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                isActive={editorState?.isBulletList}
                disabled={!editor}
                title="Bullet List"
              >
                <List className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                isActive={editorState?.isOrderedList}
                disabled={!editor}
                title="Numbered List"
              >
                <ListOrdered className="w-4 h-4" />
              </ToolbarButton>
              <div className="w-px h-6 note-toolbar-divider mx-1" />
              <ToolbarButton
                onClick={() => editor?.chain().focus().undo().run()}
                disabled={!editorState?.canUndo}
                title="Undo"
              >
                <Undo className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor?.chain().focus().redo().run()}
                disabled={!editorState?.canRedo}
                title="Redo"
              >
                <Redo className="w-4 h-4" />
              </ToolbarButton>
            </div>
            <EditorContent editor={editor} className="note-editor-body" />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !editor || !hasContent}
              isLoading={isSubmitting}
              className="flex items-center justify-center gap-2 h-9"
            >
              <Send className="w-4 h-4" />
              Add note
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!noteToDelete}
        onClose={() => setNoteToDelete(null)}
        onConfirm={handleDeleteNote}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default NotesTab;
